import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { SportDisplay } from '@/api/types';
import { formatCurrency } from '@/lib/format';
import { useVenueContext } from '@/providers/VenueProvider';

type Props = {
  sport: SportDisplay;
};

export default function SportCard({ sport }: Props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { currentSlug } = useVenueContext();
  const venueSlug = slug ?? currentSlug;
  const bookHref = `/venue/${venueSlug}/book/${sport.code}`;

  return (
    <Link to={bookHref}>
      <Card
        className="overflow-hidden transition-all duration-200 hover:scale-[1.02] hover:shadow-xl"
        style={{ borderTopColor: sport.color, borderTopWidth: 6 }}
      >
        <CardContent className="p-5">
          <div className="mb-3 flex items-start justify-between">
            <span className="text-3xl">{sport.icon}</span>
            <Badge variant="secondary" className="text-xs">
              {sport.availableUnits} {sport.availableUnits === 1 ? sport.unitTerm : sport.unitTermPlural}
            </Badge>
          </div>
          <h3 className="mb-1 text-lg font-semibold truncate">{t(`sport_${sport.code}`, { defaultValue: sport.name })}</h3>
          <p className="mb-3 text-sm text-muted-foreground line-clamp-2">
            {sport.description}
          </p>
          <div className="mb-4 flex items-center justify-between">
            <span className="text-sm font-medium text-primary whitespace-nowrap">
              {t('booking.from_price', { price: formatCurrency(sport.startingPrice, sport.currency, i18n.language) })}
            </span>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {sport.minPlayers}–{sport.maxPlayers} {t('booking.players', { count: sport.maxPlayers }).replace(`${sport.maxPlayers} `, '')}
            </span>
          </div>
          <Button
            className="w-full text-white whitespace-nowrap min-w-[100px]"
            style={{ backgroundColor: sport.color }}
            onClick={() => navigate(bookHref)}
          >
            {t('common.book_now')}
          </Button>
        </CardContent>
      </Card>
    </Link>
  );
}
